import {Injectable} from '@angular/core';
import {NavigationExtras, Router} from '@angular/router';
import {RoutePathNames, RoutePaths} from '../../app.routes';
import {AbstractNavigationService} from 'patja-ui';

@Injectable({
  providedIn: 'root',
})
export class NavigationService extends AbstractNavigationService {
  constructor(private router: Router) {
    super();
  }

  goToLogin(): void {
    this.router.navigateByUrl(`${RoutePaths.Login}`);
  }

  goToResetPassword(email?: string): void {
    const navigationExtras: NavigationExtras = {
      state: {
        email: email,
      },
    };
    this.router.navigateByUrl(RoutePaths.ResetPassword, navigationExtras);
  }

  goToHome(shouldReload?: boolean): void {
    this.router.navigateByUrl(`${RoutePaths.Home}`).then(() => {
      if (shouldReload) {
        location.reload();
      }
    });
  }

  goToCreateAccountingOffice(): void {
    this.router.navigateByUrl(`${RoutePaths.AccountingOffices}/new`);
  }

  goToEditAccountingOffice(accountingOfficeId: string): void {
    this.router.navigateByUrl(`${RoutePaths.AccountingOffices}/${accountingOfficeId}/edit`);
  }

  goToAccountingOffices(): void {
    this.router.navigateByUrl(`${RoutePaths.AccountingOffices}`);
  }

  goToAccountingOffice(accountingOfficeId: string): void {
    this.router.navigateByUrl(`${RoutePaths.AccountingOffices}/${accountingOfficeId}`);
  }

  goToCompanies(): void {
    this.router.navigateByUrl(`${RoutePaths.Companies}`);
  }

  goToCreateCompany(): void {
    this.router.navigateByUrl(`${RoutePaths.Companies}/new`);
  }

  goToCompany(companyId: string): void {
    this.router.navigateByUrl(`${RoutePaths.Companies}/${companyId}`);
  }

  goToNewCompany(): void {
    this.router.navigateByUrl(`${RoutePaths.Companies}/new`);
  }

  goToEditCompany(companyId: string): void {
    this.router.navigateByUrl(`${RoutePaths.Companies}/${companyId}/edit`);
  }

  goToVerify(email: string, parent: string): void {
    this.router.navigateByUrl(`${parent}/${RoutePaths.Verify}?email=${encodeURIComponent(email)}`);
  }

  goToSessionExpired(): void {
    this.router.navigateByUrl(RoutePaths.SessionExpired);
  }

  goToSettings(): void {
    this.router.navigateByUrl(`${RoutePaths.Home}/${RoutePaths.Settings}`);
  }

  goToSetNewPassword(email: string, key: string): void {
    this.router.navigateByUrl(
      `${RoutePaths.ResetPassword}/new?key=${key}&email=${encodeURIComponent(email)}`,
    );
  }

  goToLinkExpired(email: string): void {
    this.router.navigateByUrl(
      `${RoutePaths.ResetPassword}/${RoutePathNames.ResetPasswordLinkExpired}?email=${encodeURIComponent(email)}`,
    );
  }

  goToEnableTwoFactorAuth(): void {
    this.router.navigateByUrl(RoutePaths.EnableTwoFactor);
  }

  goToGoogleAuthSetup(): void {
    this.router.navigateByUrl(RoutePaths.GoogleAuthenticator);
  }

  goToVerifyGoogleAuth(): void {
    this.router.navigateByUrl(RoutePaths.VerifyGoogleAuthenticator);
  }

  goToChooseAuthMethod(): void {
    this.router.navigateByUrl(RoutePaths.ChooseMethod);
  }

  goToTwoFactorOptions(): void {
    this.router.navigateByUrl(RoutePaths.TwoFactorOptions);
  }

  goToVerifyBackupCode(): void {
    this.router.navigateByUrl(RoutePaths.VerifyBackupCode);
  }

  goToBackupCodes(): void {
    this.router.navigateByUrl(RoutePaths.TwoFactorBackupCodes);
  }

  goToPrivacyPolicy(): void {
    this.router.navigateByUrl(RoutePathNames.PrivacyPolicy);
  }

  goToTermsOfUse(): void {
    this.router.navigateByUrl(RoutePathNames.TermsOfUse);
  }

  goToUsers(): void {
    this.router.navigateByUrl(RoutePaths.Users);
  }

  goToNewUser(navigationExtras?: NavigationExtras): void {
    this.router.navigate([RoutePaths.NewUser], navigationExtras);
  }

  goToUser(userId: string): void {
    this.router.navigateByUrl(`${RoutePathNames.Users}/${userId}`);
  }

  goToEditUser(userId: string): void {
    this.router.navigateByUrl(`${RoutePathNames.Users}/${userId}/edit`);
  }

  goToCreateCompanyAccount(companyId: string): void {
    this.router.navigateByUrl(
      `${RoutePaths.Companies}/${companyId}/${RoutePathNames.Accounts}/new`,
    );
  }

  goToEditCompanyAccount(companyId: string, accountId: string): void {
    this.router.navigateByUrl(
      `${RoutePaths.Companies}/${companyId}/${RoutePathNames.Accounts}/${accountId}/edit`,
    );
  }

  goToCreateCompanyProduct(companyId: string): void {
    this.router.navigateByUrl(
      `${RoutePaths.Companies}/${companyId}/${RoutePathNames.ProductRegister}/new`,
    );
  }

  goToEditCompanyProduct(companyId: string, productId: string): void {
    this.router.navigateByUrl(
      `${RoutePaths.Companies}/${companyId}/${RoutePathNames.ProductRegister}/${productId}/edit`,
    );
  }

  goToAdminReceiptsActivity(
    scope: 'all' | 'accountingOffice',
    options?: {
      statuses?: readonly string[];
      preset?: 'today' | 'week' | 'month' | 'year' | 'all';
      accountingOfficeId?: string | null;
    },
  ): void {
    this.router.navigate(['/admin-activity/receipts'], {
      queryParams: {
        preset: options?.preset ?? 'month',
        scope,
        page: 1,
        size: 20,
        statuses: options?.statuses?.length ? options.statuses.join(',') : null,
        accountingOfficeId: options?.accountingOfficeId || null,
      },
    });
  }
}
